import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { User, Building2, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export function SettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate save
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Paramètres enregistrés');
    setLoading(false);
  };

  const getPlanDetails = (plan) => {
    switch (plan) {
      case 'pro':
        return { name: 'Pro', price: '149€/mois', color: 'text-primary' };
      case 'expert':
        return { name: 'Expert', price: '349€/mois', color: 'text-purple-600' };
      default:
        return { name: 'Novice', price: '49€/mois', color: 'text-slate-600' };
    }
  };

  const planDetails = getPlanDetails(user?.plan);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900">Paramètres</h1>
          <p className="text-slate-600 mt-2">Gérez votre profil et vos préférences</p>
        </div>

        {/* Profile Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <User className="w-5 h-5" />
              Informations personnelles
            </CardTitle>
            <CardDescription>
              Modifiez vos informations de profil
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nom complet</Label>
                  <Input
                    id="fullName"
                    defaultValue={user?.full_name}
                    placeholder="Votre nom"
                    data-testid="settings-fullname"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    defaultValue={user?.email}
                    disabled
                    className="bg-slate-50"
                    data-testid="settings-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="company">Entreprise</Label>
                <Input
                  id="company"
                  defaultValue={user?.company || ''}
                  placeholder="Nom de votre entreprise"
                  data-testid="settings-company"
                />
              </div>

              <Button type="submit" disabled={loading} data-testid="save-settings">
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  'Enregistrer'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Abonnement
            </CardTitle>
            <CardDescription>
              Gérez votre plan et votre facturation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg mb-4">
              <div>
                <p className="font-semibold text-slate-900">
                  Plan {planDetails.name}
                </p>
                <p className="text-sm text-slate-500">{planDetails.price}</p>
              </div>
              <span className={`inline-flex items-center gap-1 text-sm font-medium ${planDetails.color}`}>
                <CheckCircle2 className="w-4 h-4" />
                Actif
              </span>
            </div>

            {user?.plan === 'novice' && (
              <div className="space-y-4">
                <Separator />
                <div>
                  <h4 className="font-medium text-slate-900 mb-2">Passez au niveau supérieur</h4>
                  <p className="text-sm text-slate-600 mb-4">
                    Débloquez des analyses illimitées, la détection automatique des marchés et le radar territorial.
                  </p>
                  <div className="flex gap-3">
                    <Button variant="outline" data-testid="upgrade-pro">
                      Passer au Pro (149€/mois)
                    </Button>
                    <Button data-testid="upgrade-expert">
                      Passer à Expert (349€/mois)
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
